"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  isBefore,
  startOfDay,
} from "date-fns";
import { cn } from "@/lib/utils";
import {
  IconCalendar,
  IconClock,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";

interface CustomDateTimePickerProps {
  date: Date | null;
  onChange: (newDate: Date | null) => void;
  error?: string;
  placeholder?: string;
  minDate?: Date;
}

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  date,
  onChange,
  error,
  placeholder = "Select date & time",
  minDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(date || new Date());
  const [selectedTime, setSelectedTime] = useState({
    hours: date ? date.getHours() % 12 || 12 : 12,
    minutes: date?.getMinutes() || 0,
    period: date ? (date.getHours() >= 12 ? "PM" : "AM") : "PM",
  });
  const [isCustomTime, setIsCustomTime] = useState(false);
  const [customTime, setCustomTime] = useState({
    hours: date ? (date.getHours() % 12 || 12).toString() : "12",
    minutes: date ? date.getMinutes().toString().padStart(2, "0") : "00",
    period: date ? (date.getHours() >= 12 ? "PM" : "AM") : "PM",
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Generate time options for 12-hour format
  const hours12 = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  // Convert 12-hour format to 24-hour format
  const convertTo24Hour = (
    hours12: number | string,
    period: string
  ): number => {
    let hour = typeof hours12 === "string" ? parseInt(hours12) : hours12;
    if (period === "AM" && hour === 12) hour = 0;
    if (period === "PM" && hour !== 12) hour += 12;
    return hour;
  };

  // Validate custom input
  const isValidHour = (hour: string) => {
    const h = parseInt(hour);
    return !isNaN(h) && h >= 1 && h <= 12;
  };

  const isValidMinute = (minute: string) => {
    const m = parseInt(minute);
    return !isNaN(m) && m >= 0 && m <= 59;
  };

  const isDateDisabled = (day: Date) => {
    if (minDate) {
      return isBefore(day, startOfDay(minDate));
    }
    return false;
  };

  const formatDisplayValue = () => {
    if (!date) return placeholder;
    return format(date, "MMM dd, yyyy 'at' h:mm a");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateSelect = (selectedDate: Date) => {
    const newDate = new Date(selectedDate);
    if (isCustomTime) {
      const hour24 = convertTo24Hour(customTime.hours, customTime.period);
      newDate.setHours(hour24, parseInt(customTime.minutes), 0, 0);
    } else {
      const hour24 = convertTo24Hour(selectedTime.hours, selectedTime.period);
      newDate.setHours(hour24, selectedTime.minutes, 0, 0);
    }
    onChange(newDate);
  };

  const handleTimeChange = (
    type: "hours" | "minutes" | "period",
    value: number | string
  ) => {
    const newTime = { ...selectedTime, [type]: value };
    setSelectedTime(newTime);

    if (date) {
      const newDate = new Date(date);
      const hour24 = convertTo24Hour(newTime.hours, newTime.period);
      newDate.setHours(
        hour24,
        typeof newTime.minutes === "string"
          ? parseInt(newTime.minutes)
          : newTime.minutes,
        0,
        0
      );
      onChange(newDate);
    }
  };

  const handleCustomTimeChange = (
    type: "hours" | "minutes" | "period",
    value: string
  ) => {
    const newCustomTime = { ...customTime, [type]: value };
    setCustomTime(newCustomTime);

    if (date) {
      const newDate = new Date(date);
      const hour24 = convertTo24Hour(newCustomTime.hours, newCustomTime.period);
      newDate.setHours(hour24, parseInt(newCustomTime.minutes), 0, 0);
      onChange(newDate);
    }
  };

  const handleCustomSelect = () => {
    setIsCustomTime(true);
    // Convert current time to 12-hour format for custom input
    if (date) {
      const hour12 = date.getHours() % 12 || 12;
      const period = date.getHours() >= 12 ? "PM" : "AM";
      setCustomTime({
        hours: hour12.toString(),
        minutes: date.getMinutes().toString().padStart(2, "0"),
        period,
      });
    }
  };

  const handleNormalSelect = () => {
    setIsCustomTime(false);
    // Convert current time for normal selectors
    if (date) {
      const hour12 = date.getHours() % 12 || 12;
      const period = date.getHours() >= 12 ? "PM" : "AM";
      setSelectedTime({
        hours: hour12,
        minutes: date.getMinutes(),
        period,
      });
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Input Display */}
      <div
        className={cn(
          "px-4 py-3 rounded-md border bg-input-bgcolor border-primaryborder text-white text-sm flex items-center gap-2 cursor-pointer transition-colors hover:border-primary/50",
          error && "border-red-500",
          isOpen && "border-primary"
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <IconCalendar className="h-5 w-5 text-muted flex-shrink-0" />
        <span className={cn("flex-1", !date && "text-muted")}>
          {formatDisplayValue()}
        </span>
        <IconClock className="h-4 w-4 text-muted flex-shrink-0" />
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-input-bgcolor border border-primaryborder rounded-lg shadow-lg z-50 p-4">
          <div className="flex gap-4">
            {/* Calendar */}
            <div className="flex-1">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-1 hover:bg-background rounded text-muted hover:text-white transition-colors"
                >
                  <IconChevronLeft className="h-5 w-5" />
                </button>
                <h3 className="text-white font-medium">
                  {format(currentMonth, "MMMM yyyy")}
                </h3>
                <button
                  type="button"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-1 hover:bg-background rounded text-muted hover:text-white transition-colors"
                >
                  <IconChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <div
                    key={day}
                    className="text-xs text-muted text-start py-2 font-medium"
                  >
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {daysInMonth.map((day) => {
                  const isSelected = date && isSameDay(day, date);
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const isTodayDate = isToday(day);
                  const isDisabled = isDateDisabled(day);

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => !isDisabled && handleDateSelect(day)}
                      disabled={isDisabled}
                      className={cn(
                        "w-8 h-8 text-sm rounded transition-colors cursor-pointer -ml-2",
                        isCurrentMonth ? "text-white" : "text-muted",
                        isSelected && "bg-primary text-black",
                        !isSelected && !isDisabled && "hover:bg-background",
                        isTodayDate && !isSelected && "bg-background/50",
                        isDisabled && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {format(day, "d")}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Picker */}
            <div className="w-40 border-l border-primaryborder pl-4">
              <h4 className="text-white font-medium mb-3 text-sm">Time</h4>

              {!isCustomTime ? (
                <div className="space-y-3">
                  {/* Hours */}
                  <div>
                    <label className="text-xs text-muted block mb-1">
                      Hour (1-12)
                    </label>
                    <select
                      value={selectedTime.hours}
                      onChange={(e) =>
                        handleTimeChange("hours", parseInt(e.target.value))
                      }
                      className="w-full bg-background border border-primaryborder rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary"
                    >
                      {hours12.map((hour) => (
                        <option key={hour} value={hour}>
                          {hour.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Minutes */}
                  <div>
                    <label className="text-xs text-muted block mb-1">
                      Minutes
                    </label>
                    <select
                      value={selectedTime.minutes}
                      onChange={(e) =>
                        handleTimeChange("minutes", parseInt(e.target.value))
                      }
                      className="w-full bg-background border border-primaryborder rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary"
                    >
                      {minutes
                        .filter((_, i) => i % 5 === 0)
                        .map((minute) => (
                          <option key={minute} value={minute}>
                            {minute.toString().padStart(2, "0")}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* AM/PM Selector */}
                  <div>
                    <label className="text-xs text-muted block mb-1">
                      Period
                    </label>
                    <select
                      value={selectedTime.period}
                      onChange={(e) =>
                        handleTimeChange("period", e.target.value)
                      }
                      className="w-full bg-background border border-primaryborder rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Custom Hour Input */}
                  <div>
                    <label className="text-xs text-muted block mb-1">
                      Hour (1-12)
                    </label>
                    <input
                      type="text"
                      value={customTime.hours}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (
                          value === "" ||
                          (parseInt(value) >= 1 && parseInt(value) <= 12)
                        ) {
                          handleCustomTimeChange("hours", value);
                        }
                      }}
                      placeholder="12"
                      className={cn(
                        "w-full bg-background border border-primaryborder rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary",
                        !isValidHour(customTime.hours) &&
                          customTime.hours !== "" &&
                          "border-red-500"
                      )}
                    />
                  </div>

                  {/* Custom Minute Input */}
                  <div>
                    <label className="text-xs text-muted block mb-1">
                      Minutes (0-59)
                    </label>
                    <input
                      type="text"
                      value={customTime.minutes}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (
                          value === "" ||
                          (parseInt(value) >= 0 && parseInt(value) <= 59)
                        ) {
                          handleCustomTimeChange(
                            "minutes",
                            value.padStart(2, "0")
                          );
                        }
                      }}
                      placeholder="00"
                      className={cn(
                        "w-full bg-background border border-primaryborder rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary",
                        !isValidMinute(customTime.minutes) &&
                          customTime.minutes !== "" &&
                          "border-red-500"
                      )}
                    />
                  </div>

                  {/* AM/PM Selector */}
                  <div>
                    <label className="text-xs text-muted block mb-1">
                      Period
                    </label>
                    <select
                      value={customTime.period}
                      onChange={(e) =>
                        handleCustomTimeChange("period", e.target.value)
                      }
                      className="w-full bg-background border border-primaryborder rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Mode Toggle */}
              <div className="mt-4 space-y-1">
                <h5 className="text-xs text-muted mb-2">Input mode</h5>

                {/* Normal Select Mode */}
                <button
                  type="button"
                  onClick={handleNormalSelect}
                  className={cn(
                    "w-full text-xs text-muted hover:text-white hover:bg-background rounded px-2 py-1 transition-colors text-left",
                    !isCustomTime && "bg-background text-white"
                  )}
                >
                  Dropdown select
                </button>

                {/* Custom Option */}
                <button
                  type="button"
                  onClick={handleCustomSelect}
                  className={cn(
                    "w-full text-xs text-muted hover:text-white hover:bg-background rounded px-2 py-1 transition-colors text-left",
                    isCustomTime && "bg-background text-white"
                  )}
                >
                  Custom input
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 pt-3 border-t border-primaryborder">
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setIsOpen(false);
                setIsCustomTime(false);
              }}
              className="flex-1 px-3 py-2 text-sm text-muted hover:text-white cursor-pointer hover:bg-background rounded transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-3 py-2 text-sm bg-primary text-black rounded hover:bg-primary/80 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDateTimePicker;
